import { Download, Info, Play, RefreshCw } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  downloadChurnRecordsCsv,
  formatChurnDate,
  formatEndState,
  formatSignal,
} from '@/features/churn/churn-csv';
import { CHURN_TYPE_LABELS, type ChurnRun } from '@/features/churn/churn-types';
import {
  useChurnRecords,
  useChurnRuns,
  useCreateChurnRun,
} from '@/features/churn/useChurnAnalysis';

function statusBadgeVariant(status: ChurnRun['status']) {
  if (status === 'completed') return 'default' as const;
  if (status === 'failed') return 'destructive' as const;
  return 'secondary' as const;
}

export default function ChurnAnalysisPage() {
  const [monthsBack, setMonthsBack] = useState(6);
  const [inactiveDays, setInactiveDays] = useState(30);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const runsQuery = useChurnRuns();
  const recordsQuery = useChurnRecords(selectedRunId);
  const createRun = useCreateChurnRun();

  const runs = runsQuery.data ?? [];
  const activeRun = runs.find(
    (run) => run.status === 'pending' || run.status === 'running'
  );
  const records = recordsQuery.data ?? [];
  const selectedRun = runs.find((run) => run.id === selectedRunId) ?? null;

  useEffect(() => {
    if (selectedRunId) return;
    const latestCompleted = runs.find((run) => run.status === 'completed');
    if (latestCompleted) {
      setSelectedRunId(latestCompleted.id);
    }
  }, [runs, selectedRunId]);

  const labelCounts = useMemo(() => {
    return records.reduce(
      (counts, record) => {
        counts[record.label_status] = (counts[record.label_status] ?? 0) + 1;
        return counts;
      },
      {} as Record<string, number>
    );
  }, [records]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-1.5">
              <CardTitle>Churn Analysis</CardTitle>
              <CardDescription>
                One row per churned user with an LLM summary, key user quotes,
                and final-session classification. Same cohort logic as before;
                no aggregate charts.
              </CardDescription>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="space-y-1.5 text-sm font-medium">
                <span>Months back</span>
                <Input
                  type="number"
                  min={1}
                  max={24}
                  value={monthsBack}
                  onChange={(event) =>
                    setMonthsBack(Number(event.target.value))
                  }
                  className="w-full sm:w-28"
                />
              </label>
              <label className="space-y-1.5 text-sm font-medium">
                <span>Inactive after (days)</span>
                <Input
                  type="number"
                  min={7}
                  max={180}
                  value={inactiveDays}
                  onChange={(event) =>
                    setInactiveDays(Number(event.target.value))
                  }
                  className="w-full sm:w-36"
                />
              </label>
              <Button
                onClick={() =>
                  createRun.mutate({
                    months_back: monthsBack,
                    inactive_days: inactiveDays,
                  })
                }
                disabled={createRun.isPending || Boolean(activeRun)}
              >
                <Play className="h-4 w-4 mr-2" />
                {activeRun ? 'Run in progress...' : 'Run analysis'}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  void runsQuery.refetch();
                  void recordsQuery.refetch();
                }}
                aria-label="Refresh churn analysis"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              {selectedRunId && records.length > 0 && (
                <Button
                  variant="outline"
                  onClick={() =>
                    downloadChurnRecordsCsv(records, selectedRunId)
                  }
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {createRun.isError && (
            <div className="rounded-md border border-destructive bg-card px-4 py-3 text-sm text-destructive">
              {createRun.error instanceof Error
                ? createRun.error.message
                : 'Failed to start churn analysis'}
            </div>
          )}
          {activeRun && (
            <div className="rounded-md border bg-muted/40 px-4 py-3 text-sm">
              Analysis in progress — labeled {activeRun.processed_users} of{' '}
              {activeRun.total_users || '?'} churned users. This page refreshes
              automatically.
            </div>
          )}
          {runs.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Runs:</span>
              {runs.slice(0, 8).map((run) => (
                <button
                  key={run.id}
                  onClick={() =>
                    setSelectedRunId(run.status === 'completed' ? run.id : null)
                  }
                  className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Badge
                    variant={
                      selectedRunId === run.id
                        ? 'default'
                        : statusBadgeVariant(run.status)
                    }
                  >
                    {new Date(run.created_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}{' '}
                    · {run.status}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-4 w-4" />
            What you are looking at
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">Cohort.</strong> Same churn
            types as before: paid cancelled (Stripe period end), trial expired,
            free walked away (inactivity threshold). Internal and deleted
            accounts excluded.
          </p>
          <p>
            <strong className="text-foreground">Final session.</strong> For each
            user, the LLM reads their last product chat before churn and returns
            a short summary, verbatim user quotes, what the user&apos;s final
            messages signal, and how the session ended overall.{' '}
            <strong className="text-foreground">final_chat_id</strong> is stored
            so you can look up the chat elsewhere if needed.
          </p>
          {selectedRun && (
            <p>
              <strong className="text-foreground">This run.</strong>{' '}
              {records.length} users · {labelCounts.labeled ?? 0} labeled ·{' '}
              {labelCounts.no_chat_history ?? 0} no chat ·{' '}
              {labelCounts.failed ?? 0} failed · model {selectedRun.model} ·
              window {selectedRun.params.months_back} months · inactivity{' '}
              {selectedRun.params.inactive_days} days
            </p>
          )}
        </CardContent>
      </Card>

      {recordsQuery.isLoading ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading churn records...
          </CardContent>
        </Card>
      ) : recordsQuery.isError ? (
        <Card>
          <CardContent className="py-6">
            <div className="rounded-md border border-destructive bg-card px-4 py-3 text-sm text-destructive">
              {recordsQuery.error instanceof Error
                ? recordsQuery.error.message
                : 'Failed to fetch churn records'}
            </div>
          </CardContent>
        </Card>
      ) : !selectedRunId ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No completed churn analysis yet. Start one with &quot;Run
            analysis&quot;.
          </CardContent>
        </Card>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No churned users in this run.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Churned users</CardTitle>
            <CardDescription>
              {records.length} rows. Scroll horizontally on narrow screens.
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Churn type</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Churn date</TableHead>
                  <TableHead>Reason?</TableHead>
                  <TableHead>Chat ID</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>User quotes</TableHead>
                  <TableHead>Final signal</TableHead>
                  <TableHead>Session end</TableHead>
                  <TableHead>Task</TableHead>
                  <TableHead>Msgs</TableHead>
                  <TableHead>Downvote</TableHead>
                  <TableHead>Gap (days)</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="whitespace-nowrap font-medium">
                      {record.user_email}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {CHURN_TYPE_LABELS[record.churn_type]}
                    </TableCell>
                    <TableCell>{record.plan_at_churn}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatChurnDate(record.churn_date)}
                    </TableCell>
                    <TableCell>
                      {record.reason_submitted ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-[120px] truncate">
                      {record.final_chat_id ?? '—'}
                    </TableCell>
                    <TableCell className="min-w-[240px] max-w-sm text-sm">
                      {record.session_summary ?? '—'}
                    </TableCell>
                    <TableCell className="min-w-[200px] max-w-xs text-sm">
                      {record.key_user_quotes.length > 0 ? (
                        <ul className="list-disc space-y-1 pl-4">
                          {record.key_user_quotes.map((quote, index) => (
                            <li key={`${record.id}-quote-${index}`}>{quote}</li>
                          ))}
                        </ul>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatSignal(record.final_user_signal)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatEndState(record.session_end_state)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {record.task_category?.replace(/_/g, ' ') ?? '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {record.final_session_message_count}
                    </TableCell>
                    <TableCell>
                      {record.final_session_downvoted ? 'Yes' : 'No'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {record.days_from_last_message_to_churn ?? '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {record.label_status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
